import React, { useMemo } from 'react';
import { motion } from 'framer-motion';

// Tech / course logos with reliable CDN SVG URLs
const LOGOS = [
  { name: 'AWS', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg', bg: '#232F3E' },
  { name: 'Python', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg', bg: '#FFFFFF' },
  { name: 'Docker', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original.svg', bg: '#FFFFFF' },
  { name: 'Kubernetes', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/kubernetes/kubernetes-plain.svg', bg: '#FFFFFF' },
  { name: 'React', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg', bg: '#20232A' },
  { name: 'Azure', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/azure/azure-original.svg', bg: '#FFFFFF' },
  { name: 'TensorFlow', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/tensorflow/tensorflow-original.svg', bg: '#FFFFFF' },
  { name: 'MongoDB', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mongodb/mongodb-original.svg', bg: '#FFFFFF' },
  { name: 'GCP', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/googlecloud/googlecloud-original.svg', bg: '#FFFFFF' },
  { name: 'Cisco', url: 'https://www.vectorlogo.zone/logos/cisco/cisco-icon.svg', bg: '#FFFFFF' },
  { name: 'Java', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/java/java-original.svg', bg: '#FFFFFF' },
  { name: 'Linux', url: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/linux/linux-original.svg', bg: '#FFFFFF' },
];

// Layout coordinates as percentage of the container size.
// Carefully placed so cards don't overlap and cover full area.
const LAYOUT = [
  { x: 8, y: 6, size: 66, delay: 0 },
  { x: 74, y: 4, size: 78, delay: 0.4 },
  { x: 38, y: 12, size: 60, delay: 0.8 },
  { x: 62, y: 22, size: 72, delay: 0.2 },
  { x: 12, y: 30, size: 68, delay: 0.6 },
  { x: 86, y: 32, size: 62, delay: 1.0 },
  { x: 30, y: 42, size: 80, delay: 0.3 },
  { x: 66, y: 52, size: 70, delay: 0.7 },
  { x: 8, y: 58, size: 64, delay: 1.1 },
  { x: 48, y: 72, size: 74, delay: 0.5 },
  { x: 82, y: 68, size: 66, delay: 0.9 },
  { x: 20, y: 82, size: 60, delay: 0.1 },
];

function FloatCard({ logo, layout }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.6 }}
      animate={{
        opacity: 1,
        y: [0, -14, 0, 12, 0],
        x: [0, 6, 0, -6, 0],
        rotate: [-4, 4, -4],
        scale: 1,
      }}
      transition={{
        opacity: { duration: 0.6, delay: layout.delay },
        scale: { duration: 0.6, delay: layout.delay },
        y: { duration: 6 + (layout.delay * 2), repeat: Infinity, ease: 'easeInOut', delay: layout.delay },
        x: { duration: 5 + (layout.delay * 2), repeat: Infinity, ease: 'easeInOut', delay: layout.delay },
        rotate: { duration: 8 + (layout.delay * 3), repeat: Infinity, ease: 'easeInOut', delay: layout.delay },
      }}
      whileHover={{ scale: 1.15, rotate: 0, transition: { duration: 0.2 } }}
      className="absolute"
      style={{
        left: `${layout.x}%`,
        top: `${layout.y}%`,
        width: `${layout.size}px`,
        height: `${layout.size}px`,
      }}
    >
      <div
        className="w-full h-full rounded-2xl grid place-items-center p-2.5 shadow-[0_10px_40px_-12px_rgba(107,71,220,0.45)] border border-white/70 backdrop-blur-md cursor-pointer transition-shadow hover:shadow-[0_20px_50px_-10px_rgba(255,87,34,0.55)]"
        style={{ background: logo.bg === '#FFFFFF' ? 'rgba(255,255,255,0.95)' : logo.bg }}
      >
        <img src={logo.url} alt={logo.name} className="w-full h-full object-contain" loading="lazy" onError={(e) => { e.currentTarget.style.opacity = '0.3'; }} />
      </div>
    </motion.div>
  );
}

export default function HeroFloatingLogos() {
  const items = useMemo(() => LOGOS.slice(0, LAYOUT.length).map((logo, i) => ({ logo, layout: LAYOUT[i] })), []);

  return (
    <div className="relative w-full h-full">
      {/* Decorative glowing orbs behind logos */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-56 h-56 bg-gradient-to-br from-[#6b47dc]/30 to-transparent rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-gradient-to-br from-[#ff5722]/30 to-transparent rounded-full blur-3xl" style={{ animation: 'pulse 3s ease-in-out infinite 1.5s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 bg-gradient-to-br from-yellow-300/25 to-transparent rounded-full blur-2xl" />
      </div>

      {/* Central hero badge (learn) */}
      <motion.div
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.2, duration: 0.6 }}
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 pointer-events-none"
      >
        <div className="relative">
          <div className="w-32 h-32 rounded-full bg-gradient-to-br from-[#6b47dc] via-[#a855f7] to-[#ff5722] grid place-items-center shadow-2xl pulse-glow">
            <div className="text-center text-white">
              <div className="text-[10px] font-bold tracking-widest opacity-80">MASTER</div>
              <div className="text-2xl font-extrabold">200+</div>
              <div className="text-[10px] font-bold tracking-widest opacity-80">SKILLS</div>
            </div>
          </div>
          {/* Rotating ring */}
          <div className="absolute -inset-3 rounded-full border-2 border-dashed border-[#6b47dc]/40 spinning-slow" />
          <div className="absolute -inset-6 rounded-full border border-[#ff5722]/30 spinning-slow" style={{ animationDirection: 'reverse', animationDuration: '30s' }} />
        </div>
      </motion.div>

      {/* Floating logo cards */}
      {items.map((item, i) => (
        <FloatCard key={item.logo.name} logo={item.logo} layout={item.layout} />
      ))}

      {/* Connecting subtle grid pattern */}
      <div className="absolute inset-0 pointer-events-none opacity-30 dot-pattern rounded-3xl" />
    </div>
  );
}
